'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  FIELD_LABEL_CLASS,
  FilterCheckbox,
  FilterLabel,
  INPUT_PLACEHOLDER_CLASS,
} from '../FilterControls';
import { FilterGroup } from '../FilterGroup';
import type { StagedFilters, StagedFilterSetters } from '../useSidebarFilterState';
import { FilterRangeInput } from '../FilterRangeInput';
import { personAgeRanges } from '../searchConstants';

interface PeopleFilterGroupProps {
  t: TFunction<'translation', undefined>;
  peopleCount: number;
  clearPeople: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | null;
  lockOf: (control: string) => 'user' | null;
  /** The staged filter values and their setters (useSidebarFilterValues). */
  staged: StagedFilters;
  set: StagedFilterSetters;
  handleFormSubmit: () => void;
  ageOpen: boolean;
  setAgeOpen: Dispatch<SetStateAction<boolean>>;
  getAgeRangeLabel: (id: string) => string;
  toggleSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  facets: Record<string, Record<string, number>>;
  facetCount: (facet: string, value: string) => number | undefined;
}

export function PeopleFilterGroup({
  t,
  peopleCount,
  clearPeople,
  openFilterGroup,
  handleGroupOpenChange,
  groupLock,
  lockOf,
  staged,
  set,
  handleFormSubmit,
  ageOpen,
  setAgeOpen,
  getAgeRangeLabel,
  toggleSet,
  facets,
  facetCount,
}: PeopleFilterGroupProps) {
  const { personName, ageRanges: selectedAgeRanges, familyManaged, founderLed, successionRisk } = staged;
  return (
    <FilterGroup
      id="people"
      icon={Users}
      label={t('search.sidebar.groups.people', { defaultValue: 'People' })}
      activeCount={peopleCount}
      onClear={clearPeople}
      open={openFilterGroup === 'people'}
      onOpenChange={handleGroupOpenChange('people')}
      lock={groupLock('people')}
    >
      {/* Person Name */}
      <div className="space-y-1.5">
        <FilterLabel lock={lockOf('personName')} className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.personName', {
            defaultValue: 'People',
          })}
        </FilterLabel>
        <Input
          value={personName}
          disabled={!!lockOf('personName')}
          onChange={(e) => set.personName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleFormSubmit();
            }
          }}
          placeholder={t('search.sidebar.personNamePlaceholder', {
            defaultValue: 'e.g. Max Mustermann',
          })}
          className={`h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
        />
      </div>

      {/* Avg board age (avgBirthYear) */}
      <FilterRangeInput
        label={t('search.sidebar.personAge', {
          defaultValue: 'Age (people)',
        })}
        open={ageOpen}
        setOpen={setAgeOpen}
        selected={selectedAgeRanges}
        setSelected={set.ageRanges}
        ranges={personAgeRanges()}
        getLabel={(id) => getAgeRangeLabel(id)}
        toggle={(id) => toggleSet(id, set.ageRanges)}
        placeholder={t('search.sidebar.personAgePlaceholder', {
          defaultValue: 'e.g. 30 – 50',
        })}
        counts={facets['age']}
        lock={lockOf('ageRanges')}
      />

      {/* Founder / family / succession, derived from the register's
                  board history rather than read off it — so each carries a
                  hover explanation the way the badges on the company page
                  do. None of these three partition the register: a company
                  whose history we could not parse is in neither bucket, so
                  the counts do not add up to the index size.

                  The backend params stay tri-state (`founder_led=false` is
                  still valid on the API and reachable by URL); the sidebar
                  simply does not offer the negative as a checkbox. */}
      <div className="space-y-1.5">
        <FilterLabel
          lock={lockOf('familyManaged')}
          className={FIELD_LABEL_CLASS}
        >
          {t('search.sidebar.founderProfile', {
            defaultValue: 'Ownership & succession',
          })}
        </FilterLabel>
        <div className="space-y-0.5">
          <FilterCheckbox
            checked={familyManaged === true}
            onCheckedChange={(v) => set.familyManaged(v ? true : undefined)}
            lock={lockOf('familyManaged')}
            label={t('search.sidebar.familyManaged', {
              defaultValue: 'Family business',
            })}
            hint={t('search.sidebar.familyManagedHint', {
              defaultValue:
                'Two or more members of one family hold an ownership or governing role — shared surname, a surname carried in the company name, or two generations on the board.',
            })}
            count={facetCount('family_managed', 'true')}
          />
          <FilterCheckbox
            checked={founderLed === true}
            onCheckedChange={(v) => set.founderLed(v ? true : undefined)}
            lock={lockOf('founderLed')}
            label={t('search.sidebar.founderLed', {
              defaultValue: 'Founder-led',
            })}
            hint={t('search.sidebar.founderLedHint', {
              defaultValue:
                'Someone who held a role at the first register entry is still in office. Says nothing about ownership — a founder who stepped back but still owns the company does not count.',
            })}
            count={facetCount('founder_led', 'true')}
          />
          <FilterCheckbox
            checked={successionRisk === true}
            onCheckedChange={(v) => set.successionRisk(v ? true : undefined)}
            lock={lockOf('successionRisk')}
            label={t('search.sidebar.successionRisk', {
              defaultValue: 'Succession due',
            })}
            hint={t('search.sidebar.successionRiskHint', {
              defaultValue:
                'Founder-led and the longest-serving founder has reached 65. Founders with no birth date on record are excluded rather than assumed young.',
            })}
            count={facetCount('succession_risk', 'true')}
          />
        </div>
      </div>
    </FilterGroup>
  );
}
